import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BASE } from '../utils/api';
import { Bar, Pie } from 'react-chartjs-2';
import { 
  Chart as ChartJS, 
  CategoryScale, 
  LinearScale, 
  BarElement, 
  Title, 
  Tooltip, 
  Legend,
  ArcElement
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

type User = {
  id: string;
  name: string;
  email: string;
  country: string;
  phone: string;
  total_deposit_usd: number;
  total_withdrawal_usd: number;
  status: string;
};

const Users: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [snackbar, setSnackbar] = useState<string | null>(null);

  const [depositWithdrawalData, setDepositWithdrawalData] = useState({
    labels: [] as string[],
    datasets: [
      {
        label: 'Total Deposits',
        data: [] as number[],
        backgroundColor: 'rgba(79, 70, 229, 0.6)',
        borderColor: 'rgba(79, 70, 229, 1)',
        borderWidth: 1,
      },
      {
        label: 'Total Withdrawals',
        data: [] as number[],
        backgroundColor: 'rgba(156, 163, 175, 0.6)',
        borderColor: 'rgba(156, 163, 175, 1)',
        borderWidth: 1,
      },
    ],
  });

  const [userActivityData, setUserActivityData] = useState({
    labels: ['Active Users', 'Inactive Users'],
    datasets: [
      {
        data: [0, 0],
        backgroundColor: [
          'rgba(79, 70, 229, 0.8)',
          'rgba(156, 163, 175, 0.8)',
        ],
        borderColor: [
          'rgba(79, 70, 229, 1)',
          'rgba(156, 163, 175, 1)',
        ],
        borderWidth: 1,
      },
    ],
  });

  useEffect(() => {
    fetchUsersData();
  }, []);

  const normalizeUser = (u: any): User => {
    const deposit =
      Number(u.total_deposit_usd ?? u.total_deposit_usdt ?? 0) || 0;
    const withdrawal =
      Number(u.total_withdrawal_usd ?? u.total_withdrawal_usdt ?? 0) || 0;

    return {
      id: String(u.id ?? ''),
      name: String(u.name ?? '—'),
      email: String(u.email ?? '—'),
      country: String(u.country ?? ''),
      phone: String(u.phone ?? ''),
      total_deposit_usd: deposit,
      total_withdrawal_usd: withdrawal,
      status: String(u.status ?? 'active'),
    };
  };

  const fetchUsersData = async () => {
    try {
      const response = await axios.get(`${BASE}/getUserData`);
      const data = response.data;

      if (Array.isArray(data)) {
        const normalized = data.map(normalizeUser).reverse();
        setUsers(normalized);

        // Charts: use normalized data and USD fields
        const top = normalized.slice(0, 7);
        const labels = top.map(u => u.name || u.email || u.id);
        const deposits = top.map(u => u.total_deposit_usd);
        const withdrawals = top.map(u => u.total_withdrawal_usd);

        setDepositWithdrawalData({
          labels,
          datasets: [
            {
              label: 'Total Deposits',
              data: deposits,
              backgroundColor: 'rgba(79, 70, 229, 0.6)',
              borderColor: 'rgba(79, 70, 229, 1)',
              borderWidth: 1,
            },
            {
              label: 'Total Withdrawals',
              data: withdrawals,
              backgroundColor: 'rgba(156, 163, 175, 0.6)',
              borderColor: 'rgba(156, 163, 175, 1)',
              borderWidth: 1,
            },
          ],
        });

        const activeUsers = normalized.filter(u => u.total_deposit_usd > 0).length;
        const inactiveUsers = normalized.length - activeUsers;
        setUserActivityData({
          labels: ['Active Users', 'Inactive Users'],
          datasets: [
            {
              data: [activeUsers, inactiveUsers],
              backgroundColor: [
                'rgba(79, 70, 229, 0.8)',
                'rgba(156, 163, 175, 0.8)',
              ],
              borderColor: [
                'rgba(79, 70, 229, 1)',
                'rgba(156, 163, 175, 1)',
              ],
              borderWidth: 1,
            },
          ],
        });
      } else {
        console.error('Unexpected response structure:', data);
        setUsers([]);
      }
    } catch (error) {
      console.error('Failed to fetch user data:', error);
      setUsers([]);
    }
  };

  const handleDeleteUser = async (id: string, name: string) => {
    try {
      await axios.delete(`${BASE}/deleteUser`, { data: { id } });
      await fetchUsersData();
      setSnackbar(`User ${name} deleted successfully`);
      setTimeout(() => setSnackbar(null), 3000);
    } catch (error) {
      console.error('Failed to delete user:', error);
    }
  };

  const handleToggleStatus = async (id: string, currentStatus: string) => {
    const newStatus = currentStatus === 'active' ? 'suspended' : 'active';
    try {
      await axios.post(`${BASE}/user/update-status`, { id, status: newStatus });
      setUsers(prev => prev.map(u => u.id === id ? { ...u, status: newStatus } : u));
      setSnackbar(`User ${newStatus === 'suspended' ? 'suspended' : 'activated'} successfully`);
      setTimeout(() => setSnackbar(null), 3000);
    } catch (error) {
      console.error('Failed to update user status:', error);
    }
  };

  const q = searchTerm.trim().toLowerCase();
  const filteredUsers = q
    ? users.filter(u => {
        const haystack = [
          u.id || '',
          u.name || '',
          u.email || '',
          u.phone || '',
        ]
          .map(s => s.toLowerCase())
          .join(' ');
        return haystack.includes(q);
      })
    : users;

  const currency = (n: number) => `₹${(Number(n) || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
  return (
    <div className='ml-0 lg:ml-[256px] p-8 '>
      {snackbar && (
        <div className="mb-4 text-center text-white bg-green-500 py-2 px-4 rounded-lg shadow-md">
          {snackbar}
        </div>
      )}

      <h1 className="text-2xl text-[#e2e8f0] font-semibold mb-4">{`Total Users: ${users.length}`}</h1>

      <div className="mb-5 flex justify-end">
        <label htmlFor="search" className="sr-only">Search</label>
        <div className="relative w-1/2">
          <input
            type="text"
            id="search"
            className="bg-[#292d2e] w-full pl-10 pr-4 py-2 rounded-lg border border-[#2f3536] shadow-sm focus:outline-none focus:ring-2 focus:border-transparent text-[#e2e8f0] placeholder-[#94a3b8]"
            placeholder="Search by email, name or UID"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-[#94a3b8]" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
              <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
            </svg>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto relative shadow-md sm:rounded-lg bg-[#232626] hide-scrollbar" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
        <table className="w-full text-sm text-left text-[#94a3b8]">
          <thead className="text-xs text-[#e2e8f0] uppercase bg-[#1a1d1e] border-b border-[#2f3536]">
            <tr>
              <th className="py-3 px-6">S.No</th>
              <th className="py-3 px-6">Name</th>
              <th className="py-3 px-6">Email</th>
              <th className="py-3 px-6">UID</th>
              <th className="py-3 px-6">Phone</th>
              <th className="py-3 px-6">Total Deposit (INR)</th>
              <th className="py-3 px-6">Total Withdrawal (INR)</th>
              <th className="py-3 px-6">PNL (INR)</th>
              <th className="py-3 px-6">Status</th>
              <th className="py-3 px-6">Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((user, index) => {
              const pnl = (user.total_deposit_usd || 0) - (user.total_withdrawal_usd || 0);
              const pnlClass = pnl >= 0 ? 'text-[#25ee89]' : 'text-red-500';
              return (
                <tr key={`${user.id}-${index}`} className="group bg-[#232626] border-b border-[#2f3536] hover:bg-[#292d2e]">
                  <td className="text-[#e2e8f0] group-hover:text-[#25ee89] py-4 px-6">{index + 1}</td>
                  <td className="text-[#e2e8f0] group-hover:text-[#25ee89] py-4 px-6">{user.name}</td>
                  <td className="text-[#e2e8f0] group-hover:text-[#25ee89] py-4 px-6">{user.email}</td>
                  <td className="text-[#e2e8f0] group-hover:text-[#25ee89] py-4 px-6">{user.id}</td>
                  <td className="text-[#e2e8f0] group-hover:text-[#25ee89] py-4 px-6">{user.phone}</td>
                  <td className="group-hover:text-[#25ee89] py-4 px-6 text-[#25ee89]">{currency(user.total_deposit_usd)}</td>
                  <td className="group-hover:text-[#25ee89] py-4 px-6 text-red-500">{currency(user.total_withdrawal_usd)}</td>
                  <td className={`group-hover:text-[#25ee89] py-4 px-6 ${pnlClass}`}>
                    {currency(pnl)}
                  </td>
                  <td className="py-4 px-6">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      user.status === 'active' 
                        ? 'bg-[#1f3b2a] text-[#25ee89]' 
                        : 'bg-[#3b1f1f] text-red-400'
                    }`}>
                      {user.status === 'active' ? 'Active' : 'Suspended'}
                    </span>
                  </td>
                  <td className="py-4 px-6">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleToggleStatus(user.id, user.status)}
                        className={`font-medium rounded-lg text-sm px-3 py-1.5 ${
                          user.status === 'active'
                            ? 'text-white bg-yellow-600 hover:bg-yellow-700'
                            : 'text-white bg-[#25ee89] hover:bg-[#1dbf6f]'
                        }`}
                      >
                        {user.status === 'active' ? 'Suspend' : 'Activate'}
                      </button>
                      <button
                        onClick={() => handleDeleteUser(user.id, user.name)}
                        className="text-white bg-red-500 hover:bg-red-700 font-medium rounded-lg text-sm px-3 py-1.5"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {filteredUsers.length === 0 && (
              <tr>
                <td colSpan={9} className="py-6 text-center text-gray-400">No matching users.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Users;
